import React, { useCallback, useState, useEffect } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useObservableState } from 'observable-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { route, when } from '../../../../../services/utils';
import { map } from 'rxjs/operators';
import { CROP_FIELD_VIEW } from '../../../../../router/routes';
import Field from '../../../../../interfaces/field';
import { useService } from '../../../../../services/services';
import Paginate from '../../../../../interfaces/paginate';
import Form, { SubmitType } from '../../../../../components/form';
import Button from '../../../../../components/form/elements/button';
import Checkbox from '../../../../../components/form/elements/checkbox';
import Decimal from '../../../../../components/form/elements/decimal';
import Group from '../../../../../components/form/elements/group';
import Link from '../../../../../components/form/elements/link';
import Readonly from '../../../../../components/form/elements/readonly';
import Show from '../../../../../components/form/elements/show';
import Select from '../../../../../components/form/elements/select';
import Text from '../../../../../components/form/elements/text';
import TextArea from '../../../../../components/form/elements/text-area';
import Radio from '../../../../../components/form/elements/radio-group';
import css from './view.module.css';
import ClassifierGroup from '../../../../../enums/classifier-group';
import ClassifierValue from '../../../../../interfaces/classifierValue';
import Classifier from '../../../../../enums/classifier';
import TabsKeys from '../../../../../enums/tab-keys';
import Hidden from '../../../../../components/form/elements/hidden';
import { FieldStatus } from '../../../../../enums/field-status';

export default function EditField() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, farmId, season } = useParams<{
    id: string;
    farmId: string;
    season: string;
  }>();
  const { fieldService, classifierService } = useService();

  const schema = yup.object().shape({
    season_id: yup
      .number()
      .min(new Date().getFullYear() - 5)
      .max(new Date().getFullYear() + 4)
      .required(),
    lad_number: yup.string().max(255).required(),
    name: yup.string().max(255).required(),
    area: yup.number().positive().decimal().required(),
    block_number: yup.string().max(255).nullable(),
    ownership_id: when(
      (value) => !!value?.value,
      yup.object().shape({
        value: yup.string().uuid(),
      }),
      yup.mixed().notRequired()
    ),
    active: yup.boolean().nullable(),
    splitted_from_id: yup.string().max(255).nullable(),
    notes: yup.string().nullable(),
  });

  const transKeyPrefix = 'pages.crop.fields_and_works.fields.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const [field] = useObservableState<any>(
    () =>
      fieldService.show(farmId, id).pipe(
        map((field: Field) => {
          const ownership =
            field.ownership_id && field.ownership_value
              ? {
                  value: field.ownership_id,
                  label: field.ownership_value,
                }
              : null;

          return {
            ...field,
            ownership_id: ownership,
          };
        })
      ),
    null
  );

  // redirect to view page if field is not allowed to edit
  useEffect(() => {
    if (field && !fieldService.fieldEditIsAllowed(field)) {
      history.push(
        route(CROP_FIELD_VIEW, {
          farmId: farmId,
          season: season,
          id: id,
          tab: TabsKeys.basic_info,
        })
      );
    }
  }, [field, farmId, fieldService, history, id, season]);

  const submit = useCallback<SubmitType<Field>>(
    (form: any) => {
      form.ownership_id = form.ownership_id?.value;
      return [
        fieldService.update(farmId, id, form),
        (response: Field) =>
          history.push(
            route(CROP_FIELD_VIEW, {
              farmId: farmId,
              season: season,
              id: id,
              tab: TabsKeys.basic_info,
            })
          ),
      ];
    },
    [fieldService, history, farmId, season, id]
  );

  const [body] = useState(() => [
    Group([
      Hidden('season_id'),
      Text('lad_number', transKeyPrefix + 'lad_number'),
      Text('name', transKeyPrefix + 'name'),
      Show(
        Readonly([
          Select('merged_fields', {
            label: transKeyPrefix + 'merged_fields_ids',
            multi: true,
          }),
        ]),
        'merged_fields_ids',
        (value) => value !== null && typeof value !== 'undefined'
      ),
      Show(
        Readonly([
          Radio(
            'altered_for_current_season',
            [
              { value: false, label: tp('open-ended') },
              { value: true, label: tp('current-season') },
            ],
            tp('merge.deadline')
          ),
        ]),
        ['altered_for_current_season', 'field_status'],
        (value) => {
          return (
            value[0] !== null &&
            typeof value[0] !== 'undefined' &&
            [FieldStatus.CREATED_BY_MERGE, FieldStatus.MERGED_INTO].includes(
              value[1]
            )
          );
        }
      ),
      Decimal('area', transKeyPrefix + 'area'),
      Text('block_number', transKeyPrefix + 'block_number'),
      Readonly([
        Text('crop_group_value', transKeyPrefix + 'crop_group'),
        Text('crop_value', transKeyPrefix + 'crop'),
        Text('breed', transKeyPrefix + 'breed'),
      ]),
      Select('ownership_id', {
        asyncOptions: (value, loadedOptions, { page, getValues }) =>
          classifierService
            .valuesPaged(ClassifierGroup.CROP, Classifier.OWNERSHIP, {
              page,
              value,
            })
            .pipe(
              map((response: Paginate<ClassifierValue>) => {
                return {
                  hasMore: response.total === response.per_page,
                  options: response.data.map((v) => ({
                    label: v.value,
                    value: v.id,
                  })),
                  additional: { page: response.current_page + 1 },
                };
              })
            ),
        multi: false,
        label: transKeyPrefix + 'ownership',
      }),
      Checkbox('active', transKeyPrefix + 'active'),
      Show(
        Readonly([Text('merged_into_label', transKeyPrefix + 'merged_into')]),
        'merged_into_id',
        (value) => value !== null && typeof value !== 'undefined'
      ),
      Show(
        Readonly([
          Text('splitted_from_label', transKeyPrefix + 'splitted_from'),
        ]),
        'splitted_from_id',
        (value) => value !== null && typeof value !== 'undefined'
      ),
      Show(
        Readonly([
          Radio(
            'altered_for_current_season',
            [
              { value: false, label: tp('open-ended') },
              { value: true, label: tp('current-season') },
            ],
            tp('split.deadline')
          ),
        ]),
        ['altered_for_current_season', 'field_status'],
        (value) => {
          return (
            value[0] !== null &&
            typeof value[0] !== 'undefined' &&
            [FieldStatus.CREATED_BY_SPLIT, FieldStatus.SPLITTED_INTO].includes(
              value[1]
            )
          );
        }
      ),
      TextArea('notes', transKeyPrefix + 'notes'),
    ]),
  ]);

  const [footer] = useState(() => [
    Button('save'),
    Link(
      route(CROP_FIELD_VIEW, {
        farmId: farmId,
        season: season,
        id: id,
        tab: TabsKeys.basic_info,
      }),
      'cancel'
    ),
  ]);

  return (
    <Form
      title={field && tp('edit') + field.label}
      mode="create"
      body={body}
      footer={footer}
      values={field}
      submit={submit}
      schema={schema}
      className={css['field-form']}
    />
  );
}
